import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AuthAccessTokenDetails,
  OauthAEMConfig,
  TokenService,
  UserDataService
} from '@com-delta-idp/mnx-oauth';
import { handleError } from '@com-delta-idp/mnx-utils';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap } from 'rxjs/operators';
import { EdocBrands } from '../../header/navigation-book/navigation-book.model';
import {
  SavedSearchAPIRequest,
  SavedSearchAPIResponse,
  SavedSearchGQL,
  SavedSearchGQLRequest,
  SavedSearchGQLResponse,
  SavedTripResponse
} from '../../model/saved-search/saved-search.interface';
import { SavedsearchesModel } from '../../model/savedsearches.model';
import {
  createRequest,
  getSavedSearchGQLRequest
} from './saved-search-request.helper';
import { getTripList } from './saved-search-response.helper';

@Injectable({
  providedIn: 'root'
})
export class SavedSearchService {
  private savedSearches: SavedSearchGQLResponse;
  constructor(
    private http: HttpClient,
    private readonly token: TokenService,
    private userData: UserDataService
  ) {}

  handleError(error: HttpErrorResponse): Observable<never> {
    console.log('error' + error);
    handleError(error, 'header', 'shared', 'savedsearch');
    return throwError(error);
  }

  getSavedSearchFeatureFlag(): Observable<string> {
    return this.userData.getAuthConfigContent().pipe(
      mergeMap((headerConfig: OauthAEMConfig) => {
        return of(
          headerConfig['APP_SAVED_SEARCH_API_GRAPHQL'] as string | undefined
        );
      })
    );
  }

  getSavedSearch(
    saveSearchRequest: SavedSearchGQLRequest
  ): Observable<SavedTripResponse[]> {
    return this.getAccessToken().pipe(
      switchMap(
        (auth: AuthAccessTokenDetails): Observable<SavedTripResponse[]> => {
          const savedSearchPayload = createRequest(
            auth.access_token,
            saveSearchRequest
          );
          return this.retrieveSavedSearch(savedSearchPayload);
        }
      ),
      catchError(this.handleError)
    );
  }

  getSavedSearchWithResponse(
    custId: string,
    brands: EdocBrands[],
    endPoint: string
  ): Observable<SavedSearchGQLResponse> {
    if (this.savedSearches) {
      return of(this.savedSearches);
    }
    const savedSearchRequest = getSavedSearchGQLRequest(
      custId,
      brands,
      endPoint
    );
    return this.getSavedSearch(savedSearchRequest).pipe(
      map((trips: SavedTripResponse[]) => {
        const savedsSearchesParam = {
          customer: {
            savedTripList: trips
          }
        };
        this.savedSearches = {
          trips: trips,
          savedSearches: new SavedsearchesModel(savedsSearchesParam)
        } as SavedSearchGQLResponse;
        return this.savedSearches;
      }),
      catchError(this.handleError)
    );
  }

  retrieveSavedSearch(
    savedSearchPayload: SavedSearchAPIRequest
  ): Observable<SavedTripResponse[]> {
    return this.http
      .post<SavedSearchAPIResponse>(
        savedSearchPayload.endPoint,
        { query: savedSearchPayload.body },
        { headers: savedSearchPayload.headers }
      )
      .pipe(
        filter((response: SavedSearchAPIResponse) => !!response),
        map((response: SavedSearchAPIResponse) => response.data.saved_searches),
        map((searches: SavedSearchGQL[]) => {
          return getTripList(searches, savedSearchPayload.brands);
        }),
        catchError(this.handleError)
      );
  }

  private getToken(
    authConfig: OauthAEMConfig
  ): Observable<AuthAccessTokenDetails> {
    return this.userData.getAuthConfigContent().pipe(
      mergeMap((headerConfig) => {
        authConfig.CLIENT_ID_VALUE =
          headerConfig['SAVED_SEARCH_GRAPHQL_CLIENT_ID_VALUE'];
        authConfig.SCOPE_VALUE_RM =
          headerConfig['SAVED_SEARCH_GRAPHQL_SCOPE_VALUE'];
        return this.token.getAccessToken(authConfig);
      })
    );
  }

  getAccessToken(): Observable<AuthAccessTokenDetails> {
    return this.userData.getAuthConfigContent().pipe(
      filter((config) => !!config),
      switchMap((config) => {
        return from(this.getToken(config));
      }),
      filter((token) => !!token)
    );
  }
}
